import React, { useEffect, useRef } from "react";

interface DynamicReportGridProps {
    data: any[];
    columns: string[];
    maxHeight?: string;
    chartId?: string;
}

const DynamicReportGrid: React.FC<DynamicReportGridProps> = ({
                                                                 data,
                                                                 columns,
                                                                 maxHeight = "75vh",
                                                                 chartId,
                                                             }) => {
    const tableRef = useRef<HTMLTableElement | null>(null);

    // ---- Helper: Detect currency-like columns ----
    const isCurrencyColumn = (col: string): boolean => {
        const lower = col.toLowerCase();
        return (
            lower.includes("cost") ||
            lower.includes("amount") ||
            lower.includes("$") ||
            lower.includes("price") ||
            lower.includes("dollar")
        );
    };

    // ---- Helper: Detect percent-like columns ----
    const isPercentColumn = (col: string): boolean => {
        const lower = col.toLowerCase();
        return (
            lower.includes("percent") ||
            lower.includes("pct") ||
            lower.includes("rate") ||
            col.includes("%")
        );
    };

    // ---- Helper: Detect values that should align right (numeric) ----
    const isRightAlignedValue = (val: any): boolean => {
        if (val == null) return false;

        let s = String(val).trim();
        if (!s) return false;

        // Remove commas
        s = s.replace(/,/g, "");

        // Handle parentheses negatives: "(123)"
        if (/^\(.+\)$/.test(s)) {
            s = s.slice(1, -1);
        }

        // Strip currency or leading +/-
        s = s.replace(/^[-+]?[\$€£]?/, "");

        // Strip % if present
        s = s.replace(/%$/, "");

        return Number.isFinite(Number(s));
    };

    // ---------- CSV Export ----------
    const handleExportCSV = (chartId?: string) => {
        if (!data?.length) return;

        const header = columns.map((c) => `"${c.replace(/"/g, '""')}"`).join(",");

        const csvRows = data.map((row) =>
            columns
                .map((col) => `"${String(row[col] ?? "").replace(/"/g, '""')}"`)
                .join(",")
        );

        const csvContent = [header, ...csvRows].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${chartId || "report"}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // ---------- Auto-size table column widths ----------
    useEffect(() => {
        if (!tableRef.current) return;

        const table = tableRef.current;
        const headerCells = table.querySelectorAll("th");
        const bodyRows = table.querySelectorAll("tbody tr");

        headerCells.forEach((headerCell, colIndex) => {
            let maxWidth = headerCell.textContent?.length || 0;

            bodyRows.forEach((row) => {
                const cell = row.children[colIndex];
                if (cell) {
                    const len = cell.textContent?.length || 0;
                    if (len > maxWidth) maxWidth = len;
                }
            });

            (headerCell as HTMLElement).style.minWidth = `${Math.min(
                Math.max(maxWidth * 8, 100),
                400
            )}px`;
        });
    }, [data, columns]);

    return (
        <div
            className="ui segment"
            style={{
                padding: "1rem",
                background: "#111",
                borderRadius: "6px",
                overflow: "hidden",
            }}
        >
            {/* Export Button */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: "0.75rem",
                }}
            >
                <button
                    onClick={() => handleExportCSV(chartId)}
                    className="ui teal button"
                    style={{
                        backgroundColor: "#0099a8",
                        color: "#fff",
                        fontWeight: "bold",
                    }}
                >
                    <i className="download icon"></i> Export CSV
                </button>
            </div>

            {/* Scrollable table */}
            <div
                style={{
                    maxHeight,
                    overflow: "auto",
                    borderRadius: "6px",
                    border: "1px solid #2d2d2d",
                }}
            >
                <table
                    ref={tableRef}
                    style={{
                        width: "max-content",
                        minWidth: "100%",
                        borderCollapse: "collapse",
                    }}
                >
                    {/* ---------- Header ---------- */}
                    <thead>
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={col}
                                style={{
                                    background: "#00B0C7",
                                    color: "#fff",
                                    padding: "10px 14px",
                                    textAlign:
                                        isCurrencyColumn(col) || isPercentColumn(col)
                                            ? "right"
                                            : "left",
                                    fontWeight: 700,
                                    whiteSpace: "nowrap",
                                    borderBottom: "2px solid #222",
                                    borderRight: "1px solid #2d2d2d",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                }}
                            >
                                {col.replace(/_/g, " ").toUpperCase()}
                            </th>
                        ))}
                    </tr>
                    </thead>

                    {/* ---------- Body ---------- */}
                    <tbody>
                    {data.map((row, rowIndex) => (
                        <tr
                            key={rowIndex}
                            style={{
                                backgroundColor:
                                    rowIndex % 2 === 0 ? "#333" : "#222",
                                color: "#fff",
                            }}
                        >
                            {columns.map((col) => {
                                const value = row[col];

                                return (
                                    <td
                                        key={col}
                                        style={{
                                            padding: "8px 12px",
                                            textAlign:
                                                isCurrencyColumn(col) ||
                                                isPercentColumn(col) ||
                                                isRightAlignedValue(value)
                                                    ? "right"
                                                    : "left",
                                            whiteSpace: "nowrap",
                                            borderBottom: "1px solid #2d2d2d",
                                            borderRight: "1px solid #ffffff",   // <-- WHITE DIVIDER
                                        }}
                                    >
                                        {value ?? ""}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DynamicReportGrid;
